import {StorageService} from './StorageService';
import {Worklog} from './Worklog';
import {Util} from './Util';
import {I18nService} from "./I18nService";
import {WorklogStatus} from "../types/storage";

export interface DayGroup {
  key: string;
  label: string;
  items: Worklog[];
  totalSec: number;
}

export class WorklogStore {
  private items: Worklog[] = [];
  // Freeze mechanics for weekly total computation
  private static weeklyTotalFrozen = false;
  private static weeklyTotalSnapshotSec = 0;

  constructor() {
    this.reload();
  }

  static freezeWeeklyTotal(currentTotalSec: number) {
    WorklogStore.weeklyTotalFrozen = true;
    WorklogStore.weeklyTotalSnapshotSec = currentTotalSec;
  }

  static unfreezeWeeklyTotal() {
    WorklogStore.weeklyTotalFrozen = false;
  }

  reload(): void {
    // Refresh the in-memory list from persisted storage
    this.items = StorageService.loadWorklogs().map(o => new Worklog(o));
  }

  save(): void {
    StorageService.saveWorklogs(this.items.map(w => Worklog.toJSON(w)));
  }

  add(w: Worklog): void {
    this.items.unshift(w);
    this.save();
  }

  update(w: Worklog): void {
    const i = this.items.findIndex(x => x.id === w.id);
    if (i >= 0) {
      this.items[i] = w;
      this.save();
    }
  }

  remove(id: string): void {
    this.items = this.items.filter(x => x.id !== id);
    this.save();
  }

  listSorted(): Worklog[] {
    return [...this.items].sort((a, b) => b.start.getTime() - a.start.getTime());
  }

  uniqueIssueHistory(prefix?: string): string[] {
    const keys: string[] = [];
    this.items.forEach(w => {
      const k = w.issueKey;
      if (k && !keys.includes(k) && (!prefix || k.toLowerCase().includes(prefix.toLowerCase()))) {
        keys.push(k);
      }
    });
    return keys.slice(0, 10);
  }

  groupByDay(i18n: I18nService): DayGroup[] {
    const sorted = this.listSorted();
    const out: DayGroup[] = [];
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const fmtKey = (d: Date): string => d.toISOString().slice(0, 10);
    const groups = new Map<string, Worklog[]>();

    for (const w of sorted) {
      const k = fmtKey(w.start);
      if (!groups.has(k)) {
        groups.set(k, []);
      }
      groups.get(k)!.push(w);
    }

    for (const [k, arr] of groups) {
      const d = new Date(k);
      let label = k;
      if (Util.isSameDay(d, today)) {
        label = i18n.t('today');
      } else if (Util.isSameDay(d, yesterday)) {
        label = i18n.t('yesterday');
      }
      out.push({
        key: k,
        label,
        items: arr,
        totalSec: arr.reduce((s, w) => s + w.durationSec, 0)
      });
    }

    out.sort((a, b) => b.key.localeCompare(a.key));
    return out;
  }

  weeklyTotalSec(): number {
    // If totals are frozen (sync in progress), return the snapshot to avoid mid-sync flicker
    if (WorklogStore.weeklyTotalFrozen) {
      return WorklogStore.weeklyTotalSnapshotSec;
    }
    // Ensure we reflect latest persisted data (e.g., after SyncQueue writes)
    this.reload();

    const start = Util.startOfWeek(new Date());
    return this.items
      .filter(w => w.start >= start)
      .reduce((s, w) => s + w.durationSec, 0);
  }

  pendingForUpload(): Worklog[] {
    const pendingStatuses: WorklogStatus[] = ['local', 'queued', 'error'];
    return this.items.filter(w => pendingStatuses.includes(w.status));
  }
}